import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Toaster } from 'sonner';
import { Layout } from './components/layout/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/Login';
import { RegisterPage } from './pages/Register';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';

// Lazy load route components for code splitting
const ManufacturerDashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.ManufacturerDashboard })));
const ProductsListPage = lazy(() => import('./pages/Products').then(m => ({ default: m.ProductsListPage })));
const OrdersPage = lazy(() => import('./pages/Orders').then(m => ({ default: m.OrdersPage })));
const EarningsPage = lazy(() => import('./pages/Earnings').then(m => ({ default: m.EarningsPage })));
const AnalyticsPage = lazy(() => import('./pages/Analytics').then(m => ({ default: m.AnalyticsPage })));
const SettingsPage = lazy(() => import('./pages/Settings').then(m => ({ default: m.SettingsPage })));
const InventoryPage = lazy(() => import('./pages/Inventory').then(m => ({ default: m.InventoryPage })));
const MessagesPage = lazy(() => import('./pages/Messages').then(m => ({ default: m.MessagesPage })));
const ProductForm = lazy(() => import('./pages/ProductForm').then(m => ({ default: m.ProductForm })));
const BundlesListPage = lazy(() => import('./pages/Bundles').then(m => ({ default: m.BundlesListPage })));
const BundleForm = lazy(() => import('./pages/BundleForm').then(m => ({ default: m.BundleForm })));
const OrderDetailPage = lazy(() => import('./pages/OrderDetail').then(m => ({ default: m.OrderDetailPage })));

// Loading component
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
        <p className="text-gray-600 font-medium">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'manufacturer') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return <Navigate to="/login" replace />;
      }
    } catch {
      // Invalid user data, redirect to login
    }
  }
  
  return <>{children}</>;
}

function LegacyAppRedirect() {
  const location = useLocation();
  return <Navigate to={`/app${location.pathname}${location.search}`} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/dashboard/*" element={<LegacyAppRedirect />} />
          <Route path="/products/*" element={<LegacyAppRedirect />} />
          <Route path="/bundles/*" element={<LegacyAppRedirect />} />
          <Route path="/inventory/*" element={<LegacyAppRedirect />} />
          <Route path="/orders/*" element={<LegacyAppRedirect />} />
          <Route path="/analytics/*" element={<LegacyAppRedirect />} />
          <Route path="/earnings/*" element={<LegacyAppRedirect />} />
          <Route path="/messages/*" element={<LegacyAppRedirect />} />
          <Route path="/settings/*" element={<LegacyAppRedirect />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Suspense fallback={<LoadingSpinner />}><ManufacturerDashboard /></Suspense>} />
            <Route path="products" element={<Suspense fallback={<LoadingSpinner />}><ProductsListPage /></Suspense>} />
            <Route path="products/new" element={<Suspense fallback={<LoadingSpinner />}><ProductForm /></Suspense>} />
            <Route path="products/:id/edit" element={<Suspense fallback={<LoadingSpinner />}><ProductForm /></Suspense>} />
            <Route path="bundles" element={<Suspense fallback={<LoadingSpinner />}><BundlesListPage /></Suspense>} />
            <Route path="bundles/new" element={<Suspense fallback={<LoadingSpinner />}><BundleForm /></Suspense>} />
            <Route path="bundles/:id/edit" element={<Suspense fallback={<LoadingSpinner />}><BundleForm /></Suspense>} />
            <Route path="inventory" element={<Suspense fallback={<LoadingSpinner />}><InventoryPage /></Suspense>} />
            <Route path="orders" element={<Suspense fallback={<LoadingSpinner />}><OrdersPage /></Suspense>} />
            <Route path="orders/:id" element={<Suspense fallback={<LoadingSpinner />}><OrderDetailPage /></Suspense>} />
            <Route path="analytics" element={<Suspense fallback={<LoadingSpinner />}><AnalyticsPage /></Suspense>} />
            <Route path="earnings" element={<Suspense fallback={<LoadingSpinner />}><EarningsPage /></Suspense>} />
            <Route path="messages" element={<Suspense fallback={<LoadingSpinner />}><MessagesPage /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<LoadingSpinner />}><SettingsPage /></Suspense>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
